import React, { useState, useEffect } from 'react';
import { Plus, UserCheck, UserX, Eye, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import StudentRegistrationForm from '../components/students/StudentRegistrationForm';
import { ConfirmDialog, InputDialog, InfoDialog } from '../components/ui';
import { registrationApi } from '../services/api';
import { StudentRegistration, StudentRegistrationFormData } from '../types';
import { formatDate } from '../utils/helpers';

const Registrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<StudentRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const [inputDialog, setInputDialog] = useState<{
    isOpen: boolean;
    title: string;
    message?: string;
    placeholder?: string;
    defaultValue?: string;
    onSubmit: (value: string) => void;
  }>({
    isOpen: false,
    title: '',
    onSubmit: () => {}
  });

  const [infoDialog, setInfoDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type?: 'info' | 'success' | 'warning' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await registrationApi.getAll();
      setRegistrations(data);
    } catch (error) {
      toast.error('Failed to load registrations');
      console.error('Error loading registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRegistration = async (data: StudentRegistrationFormData) => {
    try {
      setSubmitting(true);
      const newRegistration = await registrationApi.create(data);
      setRegistrations(prev => [newRegistration, ...prev]);
      setShowForm(false);
      toast.success('Registration submitted successfully');
    } catch (error) {
      toast.error('Failed to submit registration');
      console.error('Error adding registration:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveRegistration = async (registration: StudentRegistration) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Approve Registration',
      message: `Are you sure you want to approve registration for ${registration.name}?\n\nThis will allow them to proceed to the admission form.`,
      type: 'info',
      onConfirm: async () => {
        try {
          await registrationApi.approve(registration.id, 'Admin');
          setRegistrations(prev =>
            prev.map(reg =>
              reg.id === registration.id
                ? {
                    ...reg,
                    registrationStatus: 'Approved' as const,
                    approvedBy: 'Admin',
                    approvedAt: new Date().toISOString(),
                  }
                : reg
            )
          );
          toast.success('Registration approved successfully');
        } catch (error) {
          toast.error('Failed to approve registration');
          console.error('Error approving registration:', error);
        }
      }
    });
  };

  const handleRejectRegistration = async (registration: StudentRegistration) => {
    setInputDialog({
      isOpen: true,
      title: 'Reject Registration',
      message: `Please enter reason for rejecting ${registration.name}'s registration:`,
      placeholder: 'Enter rejection reason...',
      onSubmit: async (rejectionReason) => {
        try {
          await registrationApi.reject(registration.id, rejectionReason);
          setRegistrations(prev =>
            prev.map(reg =>
              reg.id === registration.id
                ? {
                    ...reg,
                    registrationStatus: 'Rejected' as const,
                    rejectionReason,
                  }
                : reg
            )
          );
          toast.success('Registration rejected');
        } catch (error) {
          toast.error('Failed to reject registration');
          console.error('Error rejecting registration:', error);
        }
      }
    });
  };

  const handleDeleteRegistration = async (registration: StudentRegistration) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Registration',
      message: `Are you sure you want to delete ${registration.name}'s registration?\n\nThis action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await registrationApi.delete(registration.id);
          setRegistrations(prev => prev.filter(reg => reg.id !== registration.id));
          toast.success('Registration deleted successfully');
        } catch (error) {
          toast.error('Failed to delete registration');
          console.error('Error deleting registration:', error);
        }
      }
    });
  };

  const handleViewRegistration = (registration: StudentRegistration) => {
    const details = `Name: ${registration.name}
Registration ID: ${registration.registrationId}
Date of Birth: ${formatDate(registration.dateOfBirth)}
Gender: ${registration.gender}
Preferred Class: ${registration.preferredClass}
Parent Name: ${registration.parentName}
Parent Contact: ${registration.parentContact}
Parent Email: ${registration.parentEmail}
Status: ${registration.registrationStatus}
Submitted: ${formatDate(registration.createdAt)}
${registration.approvedBy ? `Approved by: ${registration.approvedBy}` : ''}
${registration.rejectionReason ? `Rejection Reason: ${registration.rejectionReason}` : ''}`;

    setInfoDialog({
      isOpen: true,
      title: 'Registration Details',
      message: details,
      type: 'info'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'bg-[#332711] text-[#f9daa3]';
      case 'Approved':
        return 'bg-[#173322] text-[#8ff0b8]';
      case 'Rejected':
        return 'bg-[#341a17] text-[#fbccc8]';
      default:
        return 'bg-[#b9ad9b] text-[#4a4235]';
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">New Registration</h1>
            <p className="text-[#b9ad9b] mt-1">Submit a new student registration</p>
          </div>
        </div>

        <StudentRegistrationForm
          onSubmit={handleAddRegistration}
          loading={submitting}
          onCancel={() => setShowForm(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#f3ede2]">Student Registrations</h1>
          <p className="text-[#b9ad9b] mt-1">Manage student registration applications</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Registration</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Total Registrations</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{registrations.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#16212b] rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#60a5fa]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Pending Reviews</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">
                {registrations.filter(r => r.registrationStatus === 'Pending').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#332711] rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-[#f0b64c]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Approved</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">
                {registrations.filter(r => r.registrationStatus === 'Approved').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#173322] rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-[#4ade80]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Rejected</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">
                {registrations.filter(r => r.registrationStatus === 'Rejected').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#341a17] rounded-lg flex items-center justify-center">
              <UserX className="w-6 h-6 text-[#f38b83]" />
            </div>
          </div>
        </div>
      </div>

      {/* Registrations Table */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="p-6 border-b border-[#3a3122]">
          <h2 className="text-lg font-semibold text-[#f3ede2]">Registration Applications</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : registrations.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <p className="text-[#b9ad9b] text-lg">No registrations found</p>
            <p className="text-[#8a7f6e] text-sm">Registration applications will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#211c15]">
                <tr>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Student Info</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Parent Info</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Class</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Status</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Submitted</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3122]">
                {registrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-[#28221a]">
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-[#f3ede2]">{registration.name}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.registrationId}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.gender}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-[#f3ede2]">{registration.parentName}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.parentContact}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.parentEmail}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2 py-1 bg-[#16212b] text-[#bfdbfe] rounded-full text-sm">
                        {registration.preferredClass}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(registration.registrationStatus)}`}>
                        {registration.registrationStatus}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-[#b9ad9b] text-sm">
                      {formatDate(registration.createdAt)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewRegistration(registration)}
                          className="p-2 text-[#8a7f6e] hover:text-[#60a5fa] hover:bg-[#16212b] rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {registration.registrationStatus === 'Pending' && (
                          <>
                            <button
                              onClick={() => handleApproveRegistration(registration)}
                              className="p-2 text-[#8a7f6e] hover:text-[#4ade80] hover:bg-[#173322] rounded-lg transition-colors"
                              title="Approve"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectRegistration(registration)}
                              className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                              title="Reject"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialog Components */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      <InputDialog
        isOpen={inputDialog.isOpen}
        onClose={() => setInputDialog({ ...inputDialog, isOpen: false })}
        onSubmit={inputDialog.onSubmit}
        title={inputDialog.title}
        message={inputDialog.message}
        placeholder={inputDialog.placeholder}
        defaultValue={inputDialog.defaultValue}
      />

      <InfoDialog
        isOpen={infoDialog.isOpen}
        onClose={() => setInfoDialog({ ...infoDialog, isOpen: false })}
        title={infoDialog.title}
        message={infoDialog.message}
        type={infoDialog.type}
      />
    </div>
  );
};

export default Registrations;